<template>
    <div>
        <!-- Step 1 -->
        <transition name="fade" mode="out-in">
            <div v-if="step == 1">
                <h4 class="mb-3">Calcolo Retta</h4>
                <form @submit.prevent="submitStep1">
                    <div class="p-4 p-md-5 bg-white border rounded">
                        <!-- Nome -->
                        <div class="row mb-3">
                            <label for="firstName" class="col-md-4 col-form-label text-md-end">Nome</label>
                            <div class="col-12 col-md-8">
                                <input type="text" class="form-control" id="firstName" v-model="form.first_name" :readonly="first_name" required>
                            </div>
                        </div>
                        <!-- Cognome -->
                        <div class="row mb-3">
                            <label for="lastName" class="col-md-4 col-form-label text-md-end">Cognome</label>
                            <div class="col-12 col-md-8">
                                <input type="text" class="form-control" id="lastName" v-model="form.last_name" :readonly="last_name" required>
                            </div>
                        </div>
                        <!-- Codice Fiscale -->
                        <div class="row mb-3">
                            <label for="fiscalCode" class="col-md-4 col-form-label text-md-end">Codice Fiscale</label>
                            <div class="col-12 col-md-8">
                                <input type="text" class="form-control" :class="{ 'is-invalid': !validation.fiscal_code }" id="fiscalCode" v-model="form.fiscal_code" v-uppercase  :readonly="fiscal_code" required>
                                <div class="invalid-feedback" v-if="!validation.fiscal_code">
                                    Codice Fiscale non valido o non corrispondente al nome e/o al cognome.
                                </div>
                            </div>
                        </div>
                        <!-- ECP -->
                        <div class="row mb-3">
                            <label for="ecp" class="col-md-4 col-form-label text-md-end">{{ university.ecp_text }}</label>
                            <div class="col-12 col-md-8">
                                <v-select :options="helpers.ecps" v-model="form.ecp" :clearable="false" :disabled="!!ecp"></v-select>
                            </div>
                        </div>
                        <!-- Promo Code -->
                        <div class="row mb-3">
                            <label for="label" class="col-md-4 col-form-label text-md-end">Codice Promo</label>
                            <div class="col-12 col-md-8">
                                <div class="input-group">
                                    <input 
                                        type="text" 
                                        v-model="form.promo_code" 
                                        class="form-control" 
                                        :class="{ 'is-invalid': validation.promo_code === 'fail', 'is-valid': validation.promo_code === 'success' }">
                                    <button class="btn btn-outline-secondary" type="button" @click="getPromoCode">Applica</button>
                                    <div class="invalid-feedback" v-if="validation.promo_code === 'fail'">
                                        {{ validation.promo_code_message }}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!-- Privacy -->
                        <div class="row mb-3">
                            <div class="col-12 col-md-8 offset-md-4">
                                <div class="form-check small">
                                    <input class="form-check-input" type="checkbox" v-model="form.privacy" id="privacy" value="1" required>
                                    <label class="form-check-label" for="privacy">
                                        Autorizzo al trattamento dei dati personali per finalità connesse 
                                        allo svolgimento delle attività istituzionali (obbligatorio)
                                    </label>
                                </div>
                            </div>
                        </div>
                        <!-- Marketing -->
                        <div class="row">
                            <div class="col-12 col-md-8 offset-md-4">
                                <div class="form-check small">
                                    <input class="form-check-input" type="checkbox" v-model="form.marketing" id="marketing" value="1">
                                    <label class="form-check-label" for="marketing">
                                        Autorizzo al trattamento dei dati per invio di materiale 
                                        informativo (facoltativo)
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div class="alert alert-danger mt-3" role="alert" v-if="!validation.enrol">
                            <h5><i class="fas fa-exclamation-triangle me-2"></i>Impossibile proseguire</h5>
                            <p class="mb-0">{{ validation.message }}</p>
                            <button type="button" class="btn btn-outline-danger mt-3" v-if="validation.sendEmail && !temp.sendedEmail" @click="sendEmailTemp">
                                <i class="fas fa-circle-notch fa-spin" v-if="temp.sendingEmail"></i>
                                <i class="far fa-envelope me-2" v-else></i>
                                Reinvia e-mail per l'accesso all'area personale
                            </button>
                        </div>
                        <div class="alert alert-success mt-3" v-if="temp.sendedEmail">
                            <h5><i class="fas fa-check-circle me-2"></i>E-Mail inviata</h5>
                            <p class="mb-0">Controlla la tua casella di posta elettronica.</p>
                        </div>
                    </div>
                    <div class="pt-3 text-end">
                        <button class="btn btn-success" type="submit" :disabled="helpers.sending">
                            Prosegui
                            <i class="fas fa-circle-notch fa-spin ms-2" v-if="helpers.sending"></i>
                            <i class="fas fa-angle-right ms-2" v-else></i>
                        </button>
                    </div>
                </form>
            </div>
        </transition>

        <!-- Step 2 -->
        <transition name="fade" mode="out-in">
            <div v-if="step == 2">
                <h4 class="mb-3">Dati Anagrafici</h4>
                <form @submit.prevent="submitStep2">
                    <div class="p-4 p-md-5 bg-white border rounded">
                        <div class="row mb-3">
                            <label for="gender" class="col-md-4 col-form-label text-md-end">Sesso</label>
                            <div class="col-12 col-md-8">
                                <select class="form-select" aria-label="Sesso" id="gender" v-model="form.gender" required>
                                    <option :value="null" disabled>Seleziona</option>
                                    <option value="M">Maschio</option>
                                    <option value="F">Femmina</option>
                                </select>
                            </div>
                        </div>
                        <div class="row mb-3">
                            <label for="birthDate" class="col-md-4 col-form-label text-md-end">Data di Nascita</label>
                            <div class="col-12 col-md-8">
                                <date-picker 
                                    v-model="form.birth_date" 
                                    valueType="format" 
                                    class="w-100" 
                                    placeholder="aaaa-mm-gg"
                                    :input-attr="{ required: 'required' }">
                                </date-picker>
                            </div>
                        </div>
                        <div class="row mb-3">
                            <label for="birthNation" class="col-md-4 col-form-label text-md-end">Nazione di Nascita</label>
                            <div class="col-12 col-md-8">
                                <v-select :options="helpers.birth_nations" v-model="form.birth_nation" :clearable="false"></v-select>
                            </div>
                        </div>
                        <div class="row mb-3" v-if="form.birth_nation.id == 'IT'">
                            <label for="birthProvince" class="col-md-4 col-form-label text-md-end">Provincia di Nascita</label>
                            <div class="col-12 col-md-8">
                                <v-select :options="helpers.birth_provinces" v-model="form.birth_province" :clearable="false" @input="changeBirthProvince">
                                    <template #search="{attributes, events}">
                                        <input class="vs__search" :required="!form.birth_province" v-bind="attributes" v-on="events" />
                                    </template>
                                </v-select>
                            </div>
                        </div>
                        <div class="row mb-3">
                            <label for="birthCity" class="col-md-4 col-form-label text-md-end">Comune di Nascita</label>
                            <div class="col-12 col-md-8">
                                <v-select :options="helpers.birth_cities" v-model="form.birth_city" :clearable="false" v-if="form.birth_nation.id == 'IT'">
                                    <template #search="{attributes, events}">
                                        <input class="vs__search" :required="!form.birth_city" v-bind="attributes" v-on="events" />
                                    </template>
                                </v-select>
                                <input type="text" class="form-control" v-model="form.birth_city" id="birthCity" v-else required>
                            </div>
                        </div>
                    </div>
                    <div class="pt-3 d-flex justify-content-between">
                        <button class="btn btn-secondary" type="button" @click="goBackStep">
                            <i class="fas fa-angle-left me-2"></i>Indietro
                        </button>
                        <button class="btn btn-success" type="submit">
                            Prosegui<i class="fas fa-angle-right ms-2"></i>
                        </button>
                    </div>
                </form>
            </div>
        </transition>

        <!-- Step 3 -->
        <div v-if="step == 3">
            <h4 class="mb-3">Recapiti</h4>
            <form @submit.prevent="submitStep3">
                <div class="p-4 p-md-5 bg-white border rounded">
                    <div class="row mb-3">
                        <label for="email" class="col-md-4 col-form-label text-md-end">E-Mail</label>
                        <div class="col-12 col-md-8">
                            <input type="email" class="form-control" id="email" v-model="form.email" required>
                        </div>
                    </div>
                    <div class="row mb-3">
                        <label for="email2" class="col-md-4 col-form-label text-md-end">Ripeti E-Mail</label>
                        <div class="col-12 col-md-8">
                            <input type="email" class="form-control" :class="{ 'is-invalid': !validation.email }" id="email2" v-model="form.email_2" required>
                            <div class="invalid-feedback" v-if="!validation.email">
                                La mail non corrisponde.
                            </div>
                        </div>
                    </div>
                    <div class="row mb-3">
                        <label for="telephone" class="col-md-4 col-form-label text-md-end">Recapito Telefonico</label>
                        <div class="col-12 col-md-8">
                            <input type="text" class="form-control" id="telephone" v-model="form.telephone" required>
                        </div>
                    </div>
                    <div class="row mb-3">
                        <label for="addressNation" class="col-md-4 col-form-label text-md-end">Nazione di Residenza</label>
                        <div class="col-12 col-md-8">
                            <v-select :options="helpers.address_nations" v-model="form.address_nation" :clearable="false"></v-select>
                        </div>
                    </div>
                    <div class="row mb-3" v-if="form.address_nation.id == 'IT'">
                        <label for="addressProvince" class="col-md-4 col-form-label text-md-end">Provincia di Residenza</label>
                        <div class="col-12 col-md-8">
                            <v-select :options="helpers.address_provinces" v-model="form.address_province" :clearable="false" @input="changeAddressProvince">
                                <template #search="{attributes, events}">
                                    <input class="vs__search" :required="!form.address_province" v-bind="attributes" v-on="events" />
                                </template>
                            </v-select>
                        </div>
                    </div>
                    <div class="row mb-3">
                        <label for="addressCity" class="col-md-4 col-form-label text-md-end">Comune di Residenza</label>
                        <div class="col-12 col-md-8">
                            <v-select :options="helpers.address_cities" v-model="form.address_city" :clearable="false" v-if="form.address_nation.id == 'IT'">
                            <template #search="{attributes, events}">
                                <input class="vs__search" :required="!form.address_city" v-bind="attributes" v-on="events" />
                            </template>
                        </v-select>
                            <input type="text" class="form-control" v-model="form.address_city" id="addressCity" v-else required>
                        </div>
                    </div>
                    <div class="row mb-3">
                        <label for="address" class="col-md-4 col-form-label text-md-end">Indirizzo di Residenza</label>
                        <div class="col-12 col-md-6">
                            <input type="text" class="form-control" v-model="form.address" id="address" required>
                        </div>
                        <div class="col-12 col-md-2">
                            <input type="text" class="form-control" v-model="form.address_number" placeholder="Civico" required>
                        </div>
                    </div>
                    <div class="row mb-3">
                        <label for="cap" class="col-md-4 col-form-label text-md-end">CAP</label>
                        <div class="col-12 col-md-8">
                            <input type="text" class="form-control" v-model="form.cap" id="cap" minlength="5" pattern="[0-9]{5}" required>
                        </div>
                    </div>
                    <div class="alert alert-danger mt-3" role="alert" v-if="!validation.store">
                        <h5><i class="fas fa-exclamation-triangle me-2"></i>Impossibile proseguire</h5>
                        <p class="mb-0">Si è verificato un errore, contatta il supporto tecnico!</p>
                    </div>
                </div>
                <div class="pt-3 d-flex justify-content-between">
                    <button class="btn btn-secondary" @click="goBackStep">
                        <i class="fas fa-angle-left me-2"></i>Indietro
                    </button>
                    <button class="btn btn-success" type="submit" :disabled="helpers.sending">
                        Prosegui
                        <i class="fas fa-circle-notch fa-spin ms-2" v-if="helpers.sending"></i>
                        <i class="fas fa-angle-right ms-2" v-else></i>
                    </button>
                </div>
            </form>
        </div>

        <!-- Step 4 -->
        <div v-if="step == 4">
            <h4 class="mb-3">Area personale</h4>
            <div class="p-4 p-md-5 bg-white border rounded">
                <p>Gentile <strong>{{ form.first_name }} {{ form.last_name }}</strong>,</p>
                <p>
                    la sua richiesta è andata a buon fine!<br />
                    Per poter completare la sua iscrizione le chiediamo di caricare i documenti richiesti 
                    nella sua pagina personale.
                </p>
                <a :href="access_link" target="_blank" class="btn btn-success">
                    <i class="fas fa-sign-in-alt me-2"></i>Accedi all'area personale
                </a>
            </div>
        </div>

    </div>
</template>

<script>
    import axios from 'axios'
    import vSelect from 'vue-select'
    import DatePicker from 'vue2-datepicker'
    import * as services from '../../services'

    export default {
        components: {
            vSelect,
            DatePicker
        },
        props: {
            step: {
                type: Number,
                required: true
            }
        },
        data() {
            return {
                form: {
                    first_name: null,
                    last_name: null,
                    fiscal_code: null,
                    email: null,
                    email_2: null,
                    telephone: null,
                    privacy: 1,
                    marketing: 0,
                    ecp: {},
                    gender: null,
                    birth_date: null,
                    birth_nation: { 'label': 'ITALIA', 'id': 'IT' },
                    birth_province: null,
                    birth_city: null,
                    address: null,
                    address_number: null,
                    address_nation: { 'label': 'ITALIA', 'id': 'IT' },
                    address_province: null,
                    address_city: null,
                    cap: null,
                    program: null,
                    promo_code: null,
                    supplement_program_id: null
                },
                helpers: {
                    ecps: [],
                    birth_nations: [],
                    birth_provinces: [],
                    birth_cities: [],
                    address_nations: [],
                    address_provinces: [],
                    address_cities: [],
                    sending: false
                },
                validation: {
                    email: true,
                    fiscal_code: true,
                    enrol: true,
                    message: null,
                    sendEmail: false,
                    store: true,
                    promo_code: null,
                    promo_code_message: null,
                    promo_code_type: null
                },
                temp: {
                    sendingEmail: false,
                    sendedEmail: false
                },
                access_link: null
            }
        },
        computed: {
            first_name() {
                return this.$store.getters['settings/first_name']
            },
            last_name() {
                return this.$store.getters['settings/last_name']
            },
            fiscal_code() {
                return this.$store.getters['settings/fiscal_code']
            },
            university() {
                return this.$store.getters['settings/from']
            },
            token() {
                return this.$store.getters['settings/token']
            },
            ecp() {
                return this.$store.getters['settings/ecp']
            },
            tracking() {
                return this.$store.getters['settings/tracking']
            },
            provider() {
                return this.$store.getters['settings/provider']
            },
            gads_conversion() {
                return this.$store.getters['settings/gads_conversion']
            },
            floodlight_conversion() {
                return this.$store.getters['settings/floodlight_conversion']
            },
            academic_year() {
                return this.$store.getters['settings/aa']
            },
            crm() {
                return this.$store.getters['settings/crm']
            },
            import () {
                return this.form.program?.id || 500
            },
            campaign() {
                return this.$store.getters['settings/campaign']
            }
        },
        methods: {
            async getEcp() {
                const data = await services.ecp.get('api/get/ecp-active-no-master')
                this.helpers.ecps = data.data.map(el => {
                    return {
                        'label': el.codice.toUpperCase(),
                        'id': el.user_id
                    }
                })
                if (!this.ecp) {
                    this.form.ecp = {
                        label: 'DIREZIONALE',
                        id: this.university.direzionale_id
                    }
                } else {
                    this.form.ecp = this.helpers.ecps.find(el => el.id == this.ecp)
                }
            },
            async getPromoCode() {
                if (!this.form.fiscal_code) {
                    this.validation.promo_code = 'fail'
                    this.validation.promo_code_message = 'E\' necessario il codice fiscale'
                    return
                }
                if (!this.form.promo_code) {
                    this.validation.promo_code = null
                    this.validation.promo_code_message = null
                    return
                }
                this.form.supplement_program_id = null
                const {data} = await services.ecp.post('api/check-promo-code', {
                    promo_code: this.form.promo_code,
                    fiscal_code: this.form.fiscal_code,
                    course_code: 'FIT',
                    convention_id: 432,
                    program_id: 0,
                })
                this.form.program = null
                const el = data
                this.validation.promo_code = el.status
                if (el.status == 'fail') {
                    this.validation.promo_code_message = 'Codice promo non valido'
                }
                this.validation.promo_code_type = el.type
                if (el.type == 'SupplementProgram') {
                    this.form.supplement_program_id = el.data.id
                }
                if (el.type == 'program') {
                    this.form.program = {
                        id: el.data.id,
                        label: el.data.name 
                    }
                }
            },
            async getNations() {
                const data = await services.ecp.post('api/get/nations')
                return data.data.map(el => {
                    return {
                        'label': el.NAZIONE,
                        'id': el.ID_NAZIONALITA
                    }
                })
            },
            async getProvinces() {
                const data = await services.ecp.post('api/get/province')
                return data.data.map(el => {
                    return {
                        'label': el.PROVINCIA,
                        'id': el.SIGLA
                    }
                })
            },
            async changeBirthProvince() {
                const province_id = this.form.birth_province.id
                this.form.birth_city = null
                this.helpers.birth_cities = await this.getCities(province_id)
            },
            async changeAddressProvince() {
                const province_id = this.form.address_province.id
                this.form.address_city = null
                this.helpers.address_cities = await this.getCities(province_id)
            },
            async getCities(province_id) {
                const data = await services.ecp.post('api/get/cities', {
                    province: province_id
                })
                return data.data.map(el => {
                    return {
                        'label': el.COMUNE,
                        'id': el.COD_ISTAT
                    }
                })
            },
            goBackStep() {
                const step = this.step - 1
                this.$emit('update', step)
            },
            async submitStep1() {
                this.helpers.sending = true
                /*try {
                    const check_fc = await axios.post('https://aws-gammaapi.multiversity.click/check-name-fiscal-code/eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJzdWIiOjE4NTE2LCJpc3MiOiJQRUdBU08i', {
                        fiscalCode: this.form.fiscal_code,
                        name: this.form.first_name,
                        surname: this.form.last_name
                    }, {
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    })
                    this.validation.fiscal_code = check_fc.data.data
                } catch (error) {
                    this.validation.fiscal_code = false
                }*/ 
                try {
                    const check_enrol = await services.ecp.post('api/get/fit/fiscal/code/check', {
                        fiscalCode: this.form.fiscal_code
                    })
                    this.validation.enrol = true
                } catch (error) {
                    this.validation.enrol = false
                    const response = error.response
                    switch (response.status) {
                        case 409:
                            this.validation.message = response.data
                            this.validation.sendEmail = true 
                            break 
                        case 403:
                            this.validation.message = response.data
                            break 
                        default:
                            this.validation.message = response.data
                            break
                    }
                }
                this.helpers.sending = false
                this.validation.email = this.form.email == this.form.email_2
                //if (this.validation.fiscal_code && this.validation.enrol && this.validation.email) {
                if (this.validation.enrol && this.validation.email) {    
                    if (this.tracking === true && this.$cookieconsent.acceptedCategory('marketing')) {
                        //this.trackFacebookAddToCart(this.course.code)
                        this.$trackSnapChatAddCart('OC', '24 CFU')
                    }
                    const step = this.step + 1
                    this.$emit('update', step)
                }
            },
            async sendEmailTemp() {
                this.temp.sendingEmail = true
                try {
                    const data = await services.ecp.post('api/send/ic/enrolment/online/mail', {
                        courses: this.form.courses.map(el => el.id),
                        fiscalCode: this.form.fiscal_code
                    })
                    this.temp.sendingEmail = false
                    this.temp.sendedEmail = true
                } catch(error) {
                    this.temp.sendingEmail = false
                    console.log(error)
                }
            },
            submitStep2() {
                const step = this.step + 1
                this.$emit('update', step)
            },
            async submitStep3() {
                this.helpers.sending = true
                const form = this.form
                const birth_date = form.birth_date.split('-')
                let request = {}
                let crm_id = 0

                if (this.crm === true) {
                    try {
                        const crmFields = {
                            from: this.university.crm,
                            first_name: form.first_name,
                            last_name: form.last_name,
                            email: form.email,
                            telephone: form.telephone,
                            fiscal_code: form.fiscal_code,
                            birth_date: form.birth_date,
                            gender: form.gender,
                            province: form.address_nation.id == 'IT' ? form.address_province.label : form.address_province,
                            city: form.address_nation.id == 'IT' ? form.address_city.label : form.address_city,
                            type: 4,
                            category: 12,
                            subject: '24 CFU',
                            provider: this.provider,
                            campaign: this.campaign ? this.campaign : this.$cookie('CRM_campaign', '(not set)'),
                            privacy: 1,
                            marketing: form.marketing,
                            ga_id: this.$cookie('_ga', null),
                            gclid: this.$gclid(),
                            hubspotutk: this.$cookie('hubspotutk', null),
                            code: 'SIN',
                            enrol_type: '24 CFU',
                            classform: 0,
                            ecp: form.ecp.label,
                            convention: 'FIT', 
                            program: '500', 
                            import: this.import
                        }
                        const sendToCrm = await services.crm.post('crm/public/request', crmFields)
                        request = sendToCrm.data.data.request
                        crm_id = sendToCrm.data.data.request.id
                    } catch (error) {
                        console.log(error)
                    }
                }

                try {
                    const enrolFields = {
                        ecp: form.ecp.id,
                        firstname: form.first_name,
                        lastname: form.last_name,
                        fiscalCode: form.fiscal_code,
                        email: form.email,
                        email_confirmation: form.email_2,
                        phone: form.telephone,
                        gender: form.gender,
                        birthdate: birth_date[2] + '/' + birth_date[1] + '/' + birth_date[0],
                        birthnation: form.birth_nation.id,
                        birthprovince: form.birth_nation.id == 'IT' ? form.birth_province.id : null,
                        birthcity: form.birth_nation.id == 'IT' ? form.birth_city.id : form.birth_city,
                        address: form.address,
                        address_n: form.address_number,
                        nation: form.address_nation.id,
                        province: form.address_nation.id == 'IT' ? form.address_province.id : null,
                        city: form.address_nation.id == 'IT' ? form.address_city.id : form.address_city,
                        cap: form.cap,
                        crm_id: crm_id,
                        program: form.program?.id,
                        supplement_program_id: 0,
                        promo_code: form.promo_code
                    }
                    const sendToEnrol = await services.ecp.post('api/send/fit/enrolment/online/web', enrolFields)
                    this.validation.store = true
                    this.access_link = 'https://' + this.university.api + '.multiversity.click/main/online/ic_registration.php?online_token=' + sendToEnrol.data.token 

                    if (this.tracking === true) {
                        this.$trackGoogleAnalyticsBeginCheckout('OC', this.import)
                        if (this.gads_conversion) {
                            this.$trackGoogleAdsConversion(this.gads_conversion, 'OC', this.import, '24 CFU')
                        }
                        if (this.floodlight_conversion) {
                            this.$trackFloodlightConversion(
                                this.floodlight_conversion,
                                crm_id, 
                                'OC', 
                                '24 CFU', 
                                this.import,
                                form.gender,
                                form.birthdate,
                                form.ecp.label,
                                this.academic_year,
                                this.$ga_id(),
                                request.cid
                            )
                        }
                        if (this.$cookieconsent.acceptedCategory('marketing')) {
                            this.$trackFacebookCompleteRegistration(crm_id)
                            this.$trackSnapChatPurchase('OC', '24CFU', this.import)
                            this.$trackBingSignUp('OC', this.import)
                            //this.$trackFacebookPurchase(form.course.code, form.installments.program.mp_tot)
                        }
                    }

                } catch (error) {
                    this.validation.store = false
                    console.log(error)
                }

                this.helpers.sending = false
                if (this.validation.store) {
                    const step = this.step + 1
                    this.$emit('update', step)
                }
            }
        },
        async mounted() {
            await this.getEcp()
            
            const nations = await this.getNations()
            const provinces = await this.getProvinces()
            this.helpers.birth_nations = nations
            this.helpers.address_nations = nations
            this.helpers.birth_provinces = provinces
            this.helpers.address_provinces = provinces
            if (this.first_name) this.form.first_name = this.first_name
            if (this.last_name) this.form.last_name = this.last_name
            if (this.fiscal_code) this.form.fiscal_code = this.fiscal_code
            this.$store.dispatch('summary/setImport', 500)
        },
        watch: {
            'form.ecp' (val) {
                this.$store.dispatch('summary/setEcp', val.label)
            },
            import (val) {
                this.$store.dispatch('summary/setImport', val)
            }
        }
    }
</script>